import FailedTransfersList from '@/components/payouts/failure/FailedTransfersList';
import type { InvolvedAffiliate, Transfer } from '@/types/payout';
import { Alert, Text, VStack } from '@chakra-ui/react';
import React from 'react';
import { useTranslation } from 'react-i18next';

interface PayoutChargedIncompleteAlertProps {
  failedTransfers: Transfer[];
  totalTransfers: number;
  involvedAffiliates: InvolvedAffiliate[];
}

/**
 * The batch was charged and some transfers did not go through. The commissions
 * stay attached, so neither the "reverted to pending" sentence nor a new payout
 * belongs here: offering either would pay the affiliates twice.
 */
const PayoutChargedIncompleteAlert: React.FC<PayoutChargedIncompleteAlertProps> = ({
  failedTransfers,
  totalTransfers,
  involvedAffiliates,
}) => {
  const { t } = useTranslation();
  const allFailed = failedTransfers.length === totalTransfers;

  return (
    <Alert.Root status="warning" variant="subtle">
      <Alert.Indicator />
      <Alert.Content>
        <Alert.Title>{t('payouts.failure.chargedTitle')}</Alert.Title>
        <Alert.Description>
          <VStack align="start" gap={3} mt={2}>
            <Text>
              {allFailed
                ? t('payouts.failure.summaryAll', { count: totalTransfers })
                : t('payouts.failure.summary', {
                    failed: failedTransfers.length,
                    total: totalTransfers,
                  })}
            </Text>

            <FailedTransfersList
              failedTransfers={failedTransfers}
              involvedAffiliates={involvedAffiliates}
            />

            <Text color="fg.muted" fontSize="sm">
              {t('payouts.failure.chargedDescription')}
            </Text>
          </VStack>
        </Alert.Description>
      </Alert.Content>
    </Alert.Root>
  );
};

export default PayoutChargedIncompleteAlert;
